import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Download, FileCode, Loader2, ShieldAlert, CheckCircle2 } from 'lucide-react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PhaseSteps, appendPhase } from '@/components/ui/phase-steps'
import type { RunbookTask } from '@/types/entities'
import { masterRunbooksApi, type TaskScriptResult } from '../api'

/** Where the engineer will run it - the same hosts the server writes scripts for (utils/execution-host.js). */
export const SCRIPT_HOSTS = ['jump-linux', 'workstation-windows', 'workstation-macos', 'workstation-linux'] as const

/**
 * Why these tasks cannot be scripted, or null. The server checks the same three things
 * (backend/src/services/recovery/task-group.js); saying so here saves a round trip.
 */
export function taskRangeProblem(tasks: RunbookTask[], from: number, to: number): 'order' | 'agent' | 'noCommands' | null {
    if (!(to >= from)) return 'order'
    const picked = tasks.filter((t) => (t.taskNo ?? 0) >= from && (t.taskNo ?? 0) <= to)
    if (picked.some((t) => (t.mode || 'manual').toLowerCase() === 'agent')) return 'agent'
    if (!picked.some((t) => (t.commands || '').trim())) return 'noCommands'
    return null
}

interface TaskScriptDialogProps {
    masterId: number
    tasks: RunbookTask[]
    open: boolean
    onOpenChange: (open: boolean) => void
}

/**
 * One script for several consecutive manual tasks, for an engineer doing a manual recovery.
 *
 * Andrew, 14 Oct 2026: "If the engineer is running a manual recovery it would be useful for us to
 * generate a script that could run a number of tasks. This is different from an automated recovery
 * using the restore agent." Written when asked for, from the tasks as they are on screen, and not
 * stored: the tasks stay the record of what to do.
 */
export function TaskScriptDialog({ masterId, tasks, open, onOpenChange }: TaskScriptDialogProps) {
    const { t } = useTranslation()
    const numbers = useMemo(() => tasks.map((x) => x.taskNo).filter((n): n is number => typeof n === 'number'), [tasks])
    const [from, setFrom] = useState<number>(numbers[0] ?? 1)
    const [to, setTo] = useState<number>(numbers[Math.min(numbers.length - 1, 4)] ?? 1)
    const [host, setHost] = useState<string>(SCRIPT_HOSTS[0])
    const [phases, setPhases] = useState<string[]>([])
    const [activeFile, setActiveFile] = useState<string | null>(null)

    const problem = taskRangeProblem(tasks, from, to)

    const write = useMutation({
        mutationFn: () => {
            setPhases([])
            setActiveFile(null)
            return masterRunbooksApi.taskScriptStreamed(masterId, { fromTask: from, toTask: to, hostKey: host, tasks },
                (p) => setPhases((prev) => appendPhase(prev, p)))
        },
    })
    const result: TaskScriptResult | undefined = write.data
    const shown = result?.files.find((f) => f.filePath === (activeFile ?? result.entryFile)) ?? result?.files[0]

    const download = (filePath: string, content: string) => {
        const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }))
        const a = document.createElement('a')
        a.href = url
        a.download = filePath.split('/').pop() || filePath
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!write.isPending) onOpenChange(o) }}>
            <DialogContent className="sm:max-w-[820px]" data-testid="task-script-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileCode className="h-5 w-5 text-primary" />
                        {t('masterRunbooks.taskScript.title')}
                    </DialogTitle>
                    <DialogDescription>{t('masterRunbooks.taskScript.description')}</DialogDescription>
                </DialogHeader>

                <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="task-script-from">{t('masterRunbooks.taskScript.from')}</Label>
                        <Select value={String(from)} onValueChange={(v) => setFrom(Number(v))} disabled={write.isPending}>
                            <SelectTrigger id="task-script-from"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {numbers.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="task-script-to">{t('masterRunbooks.taskScript.to')}</Label>
                        <Select value={String(to)} onValueChange={(v) => setTo(Number(v))} disabled={write.isPending}>
                            <SelectTrigger id="task-script-to"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {numbers.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="task-script-host">{t('masterRunbooks.taskScript.host')}</Label>
                        <Select value={host} onValueChange={setHost} disabled={write.isPending}>
                            <SelectTrigger id="task-script-host"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {SCRIPT_HOSTS.map((h) => <SelectItem key={h} value={h}>{t(`masterRunbooks.taskScript.hosts.${h}`)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {problem && (
                    <p role="alert" className="text-sm text-destructive" data-testid="task-script-problem">
                        {t(`masterRunbooks.taskScript.problem.${problem}`)}
                    </p>
                )}

                {phases.length > 0 && (
                    <div className="rounded-lg border bg-muted/30 px-3 py-2.5">
                        <PhaseSteps phases={phases} done={!write.isPending} className="max-h-40 overflow-y-auto" />
                    </div>
                )}
                {write.isError && (
                    <p role="alert" className="text-sm text-destructive">{(write.error as Error)?.message}</p>
                )}

                {result && shown && (
                    <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            {result.review?.verdict === 'pass'
                                ? <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400"><CheckCircle2 className="h-4 w-4" />{t('masterRunbooks.taskScript.reviewPass')}</span>
                                : <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400"><ShieldAlert className="h-4 w-4" />{t('masterRunbooks.taskScript.reviewFail')}</span>}
                            {result.invocation && <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{result.invocation}</code>}
                        </div>
                        {result.review?.issues?.length ? (
                            <ul className="list-disc pl-5 text-xs text-muted-foreground">
                                {result.review.issues.map((i, k) => <li key={k}>{i.detail}</li>)}
                            </ul>
                        ) : null}
                        <div className="flex flex-wrap gap-1">
                            {result.files.map((f) => (
                                <Button key={f.filePath} type="button" size="sm" variant={f.filePath === shown.filePath ? 'default' : 'outline'}
                                        onClick={() => setActiveFile(f.filePath)}>
                                    {f.filePath}
                                </Button>
                            ))}
                        </div>
                        <pre className="max-h-72 overflow-auto rounded-lg border bg-muted/40 p-3 text-xs" data-testid="task-script-content">{shown.content}</pre>
                        <p className="text-xs text-muted-foreground">{t('masterRunbooks.taskScript.notStored')}</p>
                    </div>
                )}

                <DialogFooter className="gap-2">
                    {result && shown && (
                        <Button type="button" variant="outline" onClick={() => download(shown.filePath, shown.content)}>
                            <Download className="mr-2 h-4 w-4" />{t('masterRunbooks.taskScript.download')}
                        </Button>
                    )}
                    <Button type="button" onClick={() => write.mutate()} disabled={write.isPending || problem != null}>
                        {write.isPending
                            ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t('masterRunbooks.taskScript.writing')}</>
                            : t('masterRunbooks.taskScript.write')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
